import clsx from 'clsx';
import type { ReactNode } from 'react';
import {
  GROUP_FORMAT_LABEL,
  GROUP_STATUS_LABEL,
  stageLabel,
  type GroupFormat,
  type GroupStatus,
} from '@doit/shared';
import type { ProjectRef } from '../api/types';

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'ok' | 'warn' | 'danger';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-canvas text-ink-mute',
    brand: 'bg-brand-50 text-brand-700',
    ok: 'bg-ok-soft text-ok',
    warn: 'bg-warn-soft text-warn',
    danger: 'bg-danger-soft text-danger',
  } as const;

  return (
    <span
      className={clsx(
        'inline-block whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const STATUS_TONE: Record<GroupStatus, 'neutral' | 'brand' | 'ok' | 'warn' | 'danger'> = {
  PLANNED: 'neutral',
  ACTIVE: 'ok',
  PAUSED: 'warn',
  FINISHED: 'brand',
  CANCELLED: 'danger',
};

export function GroupStatusBadge({ status }: { status: GroupStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{GROUP_STATUS_LABEL[status]}</Badge>;
}

export function FormatBadge({ format }: { format: GroupFormat }) {
  return <Badge>{GROUP_FORMAT_LABEL[format]}</Badge>;
}

/**
 * პროექტის ბეიჯი — ფერი თვით პროექტიდან მოდის, ამიტომ ახალი პროექტის დამატებას
 * კოდის შეცვლა არ სჭირდება.
 */
export function ProjectBadge({ project }: { project: ProjectRef }) {
  if (!project.color) return <Badge>{project.name}</Badge>;

  return (
    <span
      className="inline-block whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-medium"
      style={{ backgroundColor: `${project.color}1a`, color: project.color }}
    >
      {project.name}
    </span>
  );
}

/** ეტაპი — „II ეტაპი"; ერთეტაპიან საგანზე საერთოდ არ ჩანს */
export function StageBadge({ stage, stageCount }: { stage: number; stageCount?: number }) {
  if (stageCount !== undefined && stageCount <= 1) return null;
  return (
    <Badge tone="brand">
      {stageLabel(stage)}
      {stageCount !== undefined ? ` / ${stageCount}` : ''}
    </Badge>
  );
}

/** მიმართულების ფერადი წერტილი + სახელი */
export function DirectionTag({ name, color }: { name: string; color: string | null }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="size-2 shrink-0 rounded-full"
        style={{ background: color ?? '#cfd4dc' }}
        aria-hidden
      />
      <span className="text-ink-soft">{name}</span>
    </span>
  );
}
